import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import FolderTable from "../components/FolderTable";
import Pagination from "../components/Pagination";
import { useLoading } from "../context/LoadingContext";
import { Modal } from "bootstrap";
import useApi from "../utils/useApi";
import Toaster, { ToastContainerComponent } from "../components/Toaster"; // ✅ Toaster for notifications
import { useAuth } from "../context/AuthContext";
import InfoModal from "../components/InfoModal";
import Swal from "sweetalert2";

const PersonalFolders = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const [folders, setFolders] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDetails, setNewFolderDetails] = useState("");
  const [newAddPeopletoFolder, setnewAddPeopletoFolder] = useState("no");
  const foldersPerPage = parseInt(process.env.REACT_APP_PER_PAGE) || 50; // Default: 5 items per page
  const [totalFolders, settotalFolders] = useState(0);
  // 📌 User Selection States
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState([]); // Temp list for modal
  const [allUsers, setAllUsers] = useState([]);
  const [searchUserQuery, setSearchUserQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const [selectedFolderDetails, setSelectedFolderDetails] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const { user } = useAuth();
  
  // ✅ Fetch folders from API
  const fetchFolders = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        length: foldersPerPage,
        showOwnFoldersOnly: true,
        keyword: searchQuery || undefined,
        sort: sortOrder ?? "desc"
      };
      const response = await callApi({ endpoint: "/admin/directories/get-sub-directory", params });

      //console.log("response", response.data.data);

      if (response?.success) {
        setFolders(response.data.directories || []);
        settotalFolders(response.data.total || 0);
      } else {
        Toaster.error("Failed to load folders."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      Toaster.error("Error fetching folders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
    const modalElement = document.getElementById("addPeopleModal");

    modalElement.addEventListener("hidden.bs.modal", () => {
      document.getElementById("createFolder").style.opacity = "1"; // Remove blur when closed
    });

    return () => {
      modalElement.removeEventListener("hidden.bs.modal", () => {
        document.getElementById("createFolder").style.opacity = "1";
      });
    };
  }, [currentPage, searchQuery, sortOrder]);



  // ✅ Handle Folder Creation
  const createFolder = async () => {
    if (!newFolderName.trim()) {
      Toaster.error("Folder name is required."); // ✅ Toaster instead of alert
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/create-directory",
        method: "POST",
        data: {
          entity_name: newFolderName,
          entity_description: newFolderDetails,
          parent_directory_id: "",
          directory_owner: user.user_id
        },
      });

      if (response?.success) {

        const entityId = response.data.entity_id;
        const usersToAssign = selectedUsers.map((u) => u.user_id);

        // Reset form state
        setNewFolderName("");
        setNewFolderDetails("");
        setSelectedUsers([]);
        setTempSelectedUsers([]);

        // Force-close modal: remove all Bootstrap modal DOM state directly
        const modalElement = document.getElementById("createFolder");
        if (modalElement) {
          const existingInstance = Modal.getInstance(modalElement);
          if (existingInstance) existingInstance.dispose();

          modalElement.classList.remove("show");
          modalElement.style.display = "none";
          modalElement.removeAttribute("aria-modal");
          modalElement.setAttribute("aria-hidden", "true");
        }
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

        // Now safe to fetch & show toast
        Toaster.success("Folder created successfully!");
        fetchFolders();
        if (usersToAssign.length > 0) {
          assignFolderTouser(usersToAssign, entityId);
        }
      } else {
        Toaster.error(response?.data?.message || "Failed to create folder.");
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      Toaster.error("Error creating folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const assignFolderTouser = async (userids, entity_id) => {
    setLoading(true);
    try {
      const data = {
        directory_ids: [entity_id],
        user_ids: userids
      };
      const response = await callApi({
        endpoint: "/admin/directories/add-directory-asigned-user",
        method: "POST",
        data: data,
      });

      //console.log("response", response.data.data);

      if (response?.success) {
        Toaster.success("Users assigned to folder");
      } else {
        Toaster.error("Failed to assigned to folder."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error assigned to folder:", error);
      Toaster.error("Error assigned to folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const actionSortOrder = (action) => {
    setCurrentPage(1);
    setSortOrder(action);
  };

  // 🔹 Handle Search by Keyword
  const actionSearchQuery = (query) => {
    setCurrentPage(1);
    setSearchQuery(query);
  };

  const fetchUsers = async (keyword) => {
    if (keyword.length < 3) {
      setAllUsers([]); // Clear list if query is less than 3 characters
      return;
    }

    setIsSearching(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients",
        params: { keyword }, // Pass keyword for search
      });

      if (response?.success) {
        setAllUsers(response.data.users || []);
      } else {
        Toaster.error("Failed to load users.");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      Toaster.error("Error fetching users.");
    } finally {
      setIsSearching(false);
    }
  };

  // ✅ Open User Selection Modal
  const openUserModal = () => {
    setTempSelectedUsers([...selectedUsers]); // Copy current selections to temp
    document.getElementById("createFolder").style.opacity = "0.7";
    const modalElement = document.getElementById("addPeopleModal");
    const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
    modalInstance.show();
  };

  // ✅ Handle User Selection (Temporary Selection)
  const toggleTempUserSelection = (user) => {
    setTempSelectedUsers((prevUsers) => {
      return prevUsers.some((u) => u.user_id === user.user_id)
        ? prevUsers.filter((u) => u.user_id !== user.user_id) // Remove user
        : [...prevUsers, user]; // Add user
    });
  };

  // ✅ Assign Users from Modal to Main List
  const handleAssignUsers = () => {
    setSelectedUsers(tempSelectedUsers); // Update assigned users
    if (tempSelectedUsers.length > 0) {
      setnewAddPeopletoFolder("yes"); // Keep "Yes" selected
    } else {
      setnewAddPeopletoFolder("no"); // Auto-switch to "No" if empty
    }
    document.getElementById("createFolder").style.opacity = "1";
    // Close modal
    const modalElement = document.getElementById("addPeopleModal");
    const modalInstance = Modal.getInstance(modalElement);
    modalInstance.hide();
  };

  // ✅ Remove Assigned User
  const removeAssignedUser = (userId) => {
    setSelectedUsers((prevUsers) => {
      const updatedUsers = prevUsers.filter((u) => u.user_id !== userId);
      if (updatedUsers.length === 0) setnewAddPeopletoFolder("no"); // Auto-switch to "No" if empty
      return updatedUsers;
    });
  };


  const detailsFolder = (entity_id) => {
    navigate(`/folder-details/${entity_id}`);
  };

  const getSelectedFolderDetails = async(entity_id) => {
    setLoading(true);
    try {
      const response = await callApi({ endpoint: `/admin/directories/get-directory-info/${entity_id}` });

      if (response?.success) {
        setSelectedFolderDetails(response?.data)
        setIsInfoModalOpen(true);
      } else {
        Toaster.error("Failed to fetch folder details."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Failed to fetch folder details:", error);
      Toaster.error("Failed to fetch folder details. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  const folderDetailsModal = (entity_id) => {
    getSelectedFolderDetails(entity_id);
    
  };

  const onRename = async(newSubmitFolderName,fDetails) => {
    if (!newSubmitFolderName.trim()) {
      Toaster.error("Folder name is required."); // ✅ Toaster instead of alert
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/rename-folder",
        method: "POST",
        data: {
          entity_name: newSubmitFolderName,
          entity_id: fDetails.entity_id
        },
      });

      if (response?.success) {

        Toaster.success("Folder renamed successfully!");

        fetchFolders(); // Reload folders
        setIsInfoModalOpen(false);
      } else {
        Toaster.error(response?.data?.message || "Failed to rename folder.");
      }
    } catch (error) {
      console.error("Error rename folder:", error);
      Toaster.error("Error rename folder. Please try again.");
    } finally {
      setLoading(false);
    }
  }



  const addFolderFavorite = async(entity_id,is_favorite) =>{
    if(is_favorite === true){
      removeFavorite(entity_id);
    }else{
      addFavorite(entity_id);
    }
  };

  const removeFavorite = async(entity_id) =>{
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients/remove-user-favourite-directory",
        method: "POST",
        data: {
          entity_id: entity_id
        },
      });

      if (response?.success) {

        Toaster.success("Folder removed from favorites!");

        fetchFolders(); 
      } else {
        Toaster.error(response?.data?.message || "Failed to remove favorite folder.");
      }
    } catch (error) {
      console.error("Error to remove favorite folder:", error);
      Toaster.error("Error to remove favorite folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addFavorite = async(entity_id) =>{
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients/add-user-favourite-directory",
        method: "POST",
        data: {
          entity_id: entity_id
        },
      });

      if (response?.success) {

        Toaster.success("Folder add to favorite successfully!");

        fetchFolders(); 
      } else {
        Toaster.error(response?.data?.message || "Failed to favorite folder.");
      }
    } catch (error) {
      console.error("Error favorite folder:", error);
      Toaster.error("Error favorite folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteFolder = async (entity_id) => {
        const data = {
          entity_ids: [entity_id]
        };
        try {
          setLoading(true);
          await callApi({
            endpoint: `/admin/directories/delete-directory`,
            method: "DELETE",
            data: data,
          });

          // Remove from state
          Swal.fire("Deleted!", "Folder deleted Successfully.", "success");
          fetchFolders();
        } catch (error) {
          console.error("Error deleting folder:", error);
          Swal.fire("Error", "Failed to delete folder. Try again later.", "error");
        } finally {
          setLoading(false);
        }
      
  };



  return (
    <MainLayout>
      <ToastContainerComponent /> {/* ✅ Toaster Container for notifications */}
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="row">
                  <div className="col-md-3">
                    <div className="global_head_tag">
                      <span><i className="fa-solid fa-folder-open"></i></span> Personal Folders
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="heading_block">
                      <label>Sort:</label>
                      <select value={sortOrder} onChange={(e) => actionSortOrder(e.target.value)}>
                        <option value="desc">Descending Order</option>
                        <option value="asc">Ascending Order</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="heading_block">
                      <label>Search:</label>
                      <input
                        type="text"
                        name="fakeusernameremembered"
                        style={{ display: "none" }}
                        autoComplete="username"
                        tabIndex={-1}
                      />
                      <input
                        type="search"
                        placeholder="Search Folder"
                        autoComplete="off"
                        value={searchQuery}
                        onChange={(e) => actionSearchQuery(e.target.value)}
                      />
                      <a
                        className="add_folder"
                        href="#!"
                        data-bs-toggle="modal"
                        data-bs-target="#createFolder"
                      >
                        <span><i className="fa-solid fa-plus"></i></span> Create Folder
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="shared_folder_holder">
                <FolderTable
                  folders={folders}
                  detailsFolder={detailsFolder}
                  folderDetailsModal={folderDetailsModal}
                  addFolderFavorite = {addFolderFavorite}
                  deleteFolder = {deleteFolder}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalFolders / foldersPerPage)}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Create Folder Modal */}
      <div className="modal fade global_modal" tabIndex="-1" id="createFolder">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Folder</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <div className="form-group">
                  <label htmlFor="folderName">Name</label>
                  <input
                    type="text"
                    id="folderName"
                    className="form-control"
                    placeholder="Folder Name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="folderDetails">Details</label>
                  <textarea
                    id="folderDetails"
                    placeholder="Details"
                    className="form-control"
                    maxLength="1000"
                    value={newFolderDetails}
                    onChange={(e) => setNewFolderDetails(e.target.value)}
                  />
                  <p>Character Limit: 1000</p>
                </div>

                {/* ✅ Add People to Folder */}
                <div className="form-group">
                  <label>Add People To Folder</label>
                  <div className="chk_holder">
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="addPeople" value="yes" checked={newAddPeopletoFolder === "yes"} onChange={openUserModal} />
                      <label className="form-check-label" htmlFor="addPeopleYes">
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="addPeople"
                        id="addPeopleNo"
                        value="no"
                        checked={newAddPeopletoFolder === "" || newAddPeopletoFolder === "no"} // ✅ "No" is default
                        onChange={() => setnewAddPeopletoFolder("no")}
                      />
                      <label className="form-check-label" htmlFor="addPeopleNo">
                        No
                      </label>
                    </div>
                  </div>
                  <span className="note">* Select "Yes" to assign users, otherwise, the folder will be personal.</span>
                  {/* ✅ Show Assigned Users */}
                  {selectedUsers.length > 0 && (
                    <div className="assigned-users">
                      <strong>Assigned Users:</strong>
                      <ul>
                        {selectedUsers.map((user) => (
                          <li key={user.user_id}>
                            {user.user_name}
                            <button onClick={() => removeAssignedUser(user.user_id)}>✖</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* ✅ Buttons */}
                <div className="btn_holder">
                  <button type="button" className="btn btn-primary" onClick={createFolder}>
                    Submit
                  </button>
                  <button data-bs-dismiss="modal" className="btn btn-primary orange_btn">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* ✅ Add People Modal */}
      <div className="modal fade global_modal addpeople_modal" id="addPeopleModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content ">
            <div className="modal-header">
              <h5 className="modal-title">Select Peoples</h5>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="folderName">Search People</label>
                <input type="text" placeholder="Search Clients..." value={searchUserQuery} onChange={(e) => {
                  setSearchUserQuery(e.target.value);
                  fetchUsers(e.target.value);
                }}
                />
                {isSearching && <p>Loading users...</p>}
              </div>


              {/* User Selection */}
              <ul className="search_ppl">
                {allUsers.map((user) => (
                  <li key={user.user_id}>
                    <input type="checkbox" checked={tempSelectedUsers.some((u) => u.user_id === user.user_id)} onChange={() => toggleTempUserSelection(user)} />
                    {user.user_name}
                  </li>
                ))}
              </ul>

              {/* ✅ Assign Button */}
              <button onClick={handleAssignUsers} className="btn btn-primary">Assign</button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Folder Details Modal */}
      {isInfoModalOpen && (
        <InfoModal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          folderDetails={selectedFolderDetails}
          onRename = {onRename}
        />
      )}


    </MainLayout>
  );
};

export default PersonalFolders;
